import type React from "react";
import { useEffect, useState } from "react";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { TestId } from "../../constants/testIds";
import { useDensity } from "../../contexts/DensityContext";
import type translations from "../../translations/translations.json";
import DensitySlider from "../common/DensitySlider";
import type { ProfileSectionProps } from "./profileSectionTypes";

type TranslationKeys = keyof typeof translations.en;

const ProfileDensitySection: React.FC<ProfileSectionProps> = () => {
  const { t } = useTranslation();
  const { preference, savePreference, saving } = useDensity();
  const [desktop, setDesktop] = useState(preference.desktop);
  const [mobile, setMobile] = useState(preference.mobile);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setDesktop(preference.desktop);
    setMobile(preference.mobile);
  }, [preference]);

  const handleSave = async () => {
    setError(null);
    setSuccess(false);

    try {
      await savePreference({ desktop, mobile });
      setSuccess(true);
    } catch {
      setError(t("profile.density.saveFailed" as TranslationKeys));
    }
  };

  return (
    <Box data-testid={TestId.PROFILE.DENSITY_SECTION}>
      <Typography variant="h6" gutterBottom>
        {t("profile.density.title" as TranslationKeys)}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("profile.density.hint" as TranslationKeys)}
      </Typography>
      <Stack spacing={2}>
        <Box sx={{ width: "100%", maxWidth: 360 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {t("profile.density.desktop" as TranslationKeys)}
          </Typography>
          <DensitySlider
            variant="form"
            value={desktop}
            onChange={setDesktop}
            disabled={saving}
            testId={TestId.PROFILE.DENSITY_DESKTOP_SELECT}
          />
        </Box>
        <Box sx={{ width: "100%", maxWidth: 360 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {t("profile.density.mobile" as TranslationKeys)}
          </Typography>
          <DensitySlider
            variant="form"
            value={mobile}
            onChange={setMobile}
            disabled={saving}
            testId={TestId.PROFILE.DENSITY_MOBILE_SELECT}
          />
        </Box>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ minWidth: 100, alignSelf: "flex-start" }}
          data-testid={TestId.PROFILE.DENSITY_SAVE_BUTTON}
        >
          {t("Save" as TranslationKeys)}
        </Button>
      </Stack>
      {error ? (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mt: 1 }}>
          {t("Success" as TranslationKeys)}
        </Alert>
      ) : null}
    </Box>
  );
};

export default ProfileDensitySection;
